import React, { useState, useEffect, useRef } from "react";
import {
  AlertTriangle,
  ChevronLeft,
  Plus,
  RefreshCw,
  Upload,
  X,
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
  Hash,
  DollarSign,
  Landmark,
  CreditCard,
  MessageSquare,
  Image as ImageIcon,
  ChevronRight,
  Inbox,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { reportTransaction, getMyFailedTransactions } from "../../api/apiServices";
import { toast } from "react-toastify";

/* ─────────────────────── constants ─────────────────────── */
const PROVIDERS = ["MTN", "Airtel", "Glo", "9mobile", "Other"];
const METHODS   = ["Wallet", "Bank Transfer", "Card", "USSD", "Other"];

const STATUS_MAP = {
  pending:  { icon: Clock,         color: "text-amber-500",  bg: "bg-amber-50  border-amber-200",  label: "Pending Review" },
  resolved: { icon: CheckCircle2,  color: "text-green-500",  bg: "bg-green-50  border-green-200",  label: "Resolved"       },
  rejected: { icon: XCircle,       color: "text-red-500",    bg: "bg-red-50    border-red-200",    label: "Rejected"       },
};

const money = (v) =>
  v != null ? `₦${Number(v).toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : "₦—";
const fmt   = (v) => (v ? new Date(v).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : "—");

/* ─────────────────────── status badge ─────────────────────── */
const StatusBadge = ({ status }) => {
  const m   = STATUS_MAP[status] ?? STATUS_MAP.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${m.bg} ${m.color}`}>
      <Icon size={11} strokeWidth={2.5} /> {m.label}
    </span>
  );
};

/* ─────────────────────── detail sheet ─────────────────────── */
function DetailSheet({ report, onClose }) {
  return (
    <AnimatePresence>
      {report && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl max-h-[82vh] overflow-y-auto pb-8"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
          >
            {/* drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>

            <div className="px-5 pb-10">
              <div className="flex items-center justify-between mb-4 pt-1">
                <h3 className="text-base font-bold text-gray-800">Report Details</h3>
                <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100">
                  <X size={18} className="text-gray-500" />
                </button>
              </div>

              {/* amount hero */}
              <div className="bg-[#EBF0FA] rounded-2xl p-4 flex items-center gap-4 mb-5">
                <div className="w-11 h-11 rounded-full bg-[#3866A3]/15 flex items-center justify-center">
                  <DollarSign size={20} className="text-[#3866A3]" />
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">Reported Amount</p>
                  <p className="text-2xl font-bold text-[#3866A3]">{money(report.amount)}</p>
                </div>
                <div className="ml-auto">
                  <StatusBadge status={report.status} />
                </div>
              </div>

              {/* info rows */}
              <div className="space-y-3">
                {[
                  { icon: Hash,        label: "Reference",       val: report.transactionReference ?? "—" },
                  { icon: Landmark,    label: "Provider",        val: report.paymentProvider ?? "—"      },
                  { icon: CreditCard,  label: "Method",          val: report.paymentMethod ?? "—"        },
                  { icon: FileText,    label: "Submitted",       val: fmt(report.createdAt)               },
                ].map(({ icon: Icon, label, val }) => (
                  <div key={label} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                    <Icon size={15} className="text-gray-400 shrink-0" />
                    <span className="text-xs text-gray-400 w-24 shrink-0">{label}</span>
                    <span className="text-sm text-gray-700 font-semibold truncate">{val}</span>
                  </div>
                ))}

                {report.description && (
                  <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                    <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <MessageSquare size={10} /> Your Description
                    </p>
                    <p className="text-sm text-amber-800 leading-relaxed">{report.description}</p>
                  </div>
                )}

                {report.adminNote && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                    <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1">Admin Response</p>
                    <p className="text-sm text-blue-800 leading-relaxed">{report.adminNote}</p>
                  </div>
                )}

                {report.proofUrl && (
                  <a
                    href={report.proofUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3"
                  >
                    <ImageIcon size={15} className="text-[#3866A3]" />
                    <span className="text-sm text-[#3866A3] font-semibold">View Proof of Payment</span>
                    <ChevronRight size={14} className="text-[#3866A3] ml-auto" />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ─────────────────────── report form ─────────────────────── */
function ReportForm({ onSuccess }) {
  const [form, setForm] = useState({
    transactionReference: "",
    amount: "",
    paymentProvider: "",
    paymentMethod: "",
    description: "",
    proofUrl: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]             = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    const { transactionReference, amount, paymentProvider, paymentMethod, description } = form;
    if (!transactionReference.trim()) { toast.error("Transaction reference is required"); return; }
    if (!amount || isNaN(Number(amount))) { toast.error("Enter a valid amount"); return; }
    if (!paymentProvider) { toast.error("Select a payment provider"); return; }
    if (!paymentMethod)   { toast.error("Select a payment method"); return; }
    if (!description.trim()) { toast.error("Please describe the issue"); return; }

    setSubmitting(true);
    // const t = toast.loading("Submitting your report…");
    try {
      await reportTransaction(
        transactionReference,
        Number(amount),
        paymentProvider,
        paymentMethod,
        description,
        form.proofUrl || undefined
      );
      toast.success("Report submitted!");
      setDone(true);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center py-16 px-6 text-center"
      >
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-5">
          <CheckCircle2 size={36} className="text-green-500" />
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">Report Submitted!</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-6">
          We've received your report and will review it within 24–48 hours. Check <strong>My Reports</strong> for updates.
        </p>
        <button
          onClick={() => {
            setDone(false);
            setForm({ transactionReference: "", amount: "", paymentProvider: "", paymentMethod: "", description: "", proofUrl: "" });
          }}
          className="bg-[#3866A3] text-white px-6 py-3 rounded-xl text-sm font-semibold flex items-center gap-2"
        >
          <Plus size={16} /> Report Another
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="space-y-4 pb-6"
    >
      {/* info banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex gap-3">
        <AlertTriangle size={17} className="text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 leading-relaxed">
          Only report transactions where money left your account but the service was <strong>not delivered</strong>. False reports may result in account suspension.
        </p>
      </div>

      {/* transaction reference */}
      <Field icon={Hash} label="Transaction Reference" required>
        <input
          value={form.transactionReference}
          onChange={(e) => set("transactionReference", e.target.value)}
          placeholder="e.g. TXN-20250501-XXXXXX"
          className="w-full text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
        />
      </Field>

      {/* amount */}
      <Field icon={DollarSign} label="Amount (₦)" required>
        <input
          type="number"
          value={form.amount}
          onChange={(e) => set("amount", e.target.value)}
          placeholder="e.g. 1500"
          className="w-full text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
          inputMode="decimal"
        />
      </Field>

      {/* provider */}
      <Field icon={Landmark} label="Payment Provider" required>
        <select
          value={form.paymentProvider}
          onChange={(e) => set("paymentProvider", e.target.value)}
          className="w-full text-sm outline-none bg-transparent text-gray-800 appearance-none"
        >
          <option value="">Select provider…</option>
          {PROVIDERS.map((p) => <option key={p}>{p}</option>)}
        </select>
      </Field>

      {/* method */}
      <Field icon={CreditCard} label="Payment Method" required>
        <select
          value={form.paymentMethod}
          onChange={(e) => set("paymentMethod", e.target.value)}
          className="w-full text-sm outline-none bg-transparent text-gray-800 appearance-none"
        >
          <option value="">Select method…</option>
          {METHODS.map((m) => <option key={m}>{m}</option>)}
        </select>
      </Field>

      {/* description */}
      <Field icon={MessageSquare} label="Describe the Issue" required stacked>
        <textarea
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Explain what happened — when you paid, what service you expected, and what went wrong…"
          rows={4}
          className="w-full text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400 resize-none"
        />
      </Field>

      {/* proof URL */}
      <Field icon={ImageIcon} label="Proof of Payment URL" sublabel="Optional">
        <input
          value={form.proofUrl}
          onChange={(e) => set("proofUrl", e.target.value)}
          placeholder="https://… (screenshot link)"
          className="w-full text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
        />
      </Field>

      <motion.button
        onClick={handleSubmit}
        disabled={submitting}
        whileTap={{ scale: 0.97 }}
        className="w-full bg-[#3866A3] disabled:opacity-60 text-white py-4 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 shadow-md shadow-[#3866A3]/25 mt-2"
      >
        {submitting
          ? <><RefreshCw size={16} className="animate-spin" /> Submitting…</>
          : <><Upload size={16} /> Submit Report</>
        }
      </motion.button>
    </motion.div>
  );
}

/* tiny field wrapper */
function Field({ icon: Icon, label, required, sublabel, stacked, children }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-2xl px-4 ${stacked ? "pt-3 pb-3" : "py-3"} focus-within:border-[#3866A3] transition-colors`}>
      <div className={`flex ${stacked ? "flex-col gap-2" : "items-center gap-3"}`}>
        {!stacked && <Icon size={16} className="text-gray-400 shrink-0" />}
        <div className={`flex-1 ${stacked ? "" : ""}`}>
          <div className="flex items-center gap-1 mb-0.5">
            {stacked && <Icon size={13} className="text-gray-400" />}
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</p>
            {required && <span className="text-red-400 text-[10px]">*</span>}
            {sublabel && <span className="text-[10px] text-gray-300 ml-1">{sublabel}</span>}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── my reports list ─────────────────────── */
function MyReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await getMyFailedTransactions();
      const list = res?.data ?? res?.items ?? res ?? [];
      setReports(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Could not load your reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-[#3866A3]">
        <RefreshCw className="animate-spin mb-2" size={22} />
        <p className="text-sm font-medium text-gray-400">Loading your reports…</p>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center py-20 text-center"
      >
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Inbox size={26} className="text-gray-400" />
        </div>
        <p className="text-sm font-semibold text-gray-600 mb-1">No reports yet</p>
        <p className="text-xs text-gray-400">Reports you submit will appear here</p>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-3 pb-6"
      >
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-gray-400">{reports.length} report{reports.length !== 1 ? "s" : ""}</p>
          <button onClick={fetch} className="flex items-center gap-1 text-[11px] text-[#3866A3] font-semibold">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>

        {reports.map((r, i) => {
          const m    = STATUS_MAP[r.status] ?? STATUS_MAP.pending;
          const Icon = m.icon;
          return (
            <motion.button
              key={r._id ?? r.id ?? i}
              onClick={() => setSelected(r)}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-white border border-gray-100 rounded-2xl px-4 py-4 flex items-center gap-4 text-left shadow-sm hover:shadow-md transition-shadow"
            >
              {/* status icon */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${m.bg} shrink-0`}>
                <Icon size={18} className={m.color} strokeWidth={2} />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-gray-700 truncate">
                  {r.transactionReference ?? r.referenceId ?? "No reference"}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {r.paymentProvider} · {r.paymentMethod} · {fmt(r.createdAt)}
                </p>
              </div>

              <div className="text-right shrink-0">
                <p className="text-sm font-bold text-gray-800">{money(r.amount)}</p>
                <StatusBadge status={r.status} />
              </div>

              <ChevronRight size={14} className="text-gray-300 shrink-0" />
            </motion.button>
          );
        })}
      </motion.div>

      <DetailSheet report={selected} onClose={() => setSelected(null)} />
    </>
  );
}

/* ─────────────────────── main page ─────────────────────── */
export default function ReportTransaction() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("report"); // "report" | "history"
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* sticky header */}
      <header className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10 shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-gray-100 transition"
        >
          <ChevronLeft size={20} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-[17px] font-bold text-[#3866A3] leading-tight">Transaction Reports</h1>
          <p className="text-[11px] text-gray-400">Report failed • track status</p>
        </div>
      </header>

      {/* tab switcher */}
      <div className="px-4 pt-5 pb-2">
        <div className="bg-white border border-gray-200 rounded-2xl p-1 flex gap-1">
          {[
            { key: "report",  label: "Report Issue",  icon: AlertTriangle },
            { key: "history", label: "My Reports",    icon: FileText       },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                tab === key
                  ? "bg-[#3866A3] text-white shadow-md shadow-[#3866A3]/25"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon size={14} strokeWidth={2.5} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* content */}
      <main className="px-4 pt-3 max-w-md mx-auto">
        <AnimatePresence mode="wait">
          {tab === "report" ? (
            <motion.div
              key="report"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              <ReportForm
                onSuccess={() => {
                  setRefreshKey((k) => k + 1);
                }}
              />
            </motion.div>
          ) : (
            <motion.div
              key={`history-${refreshKey}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <MyReports />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <MobileBottomNav />
    </div>
  );
}
